import moment from "moment";
import * as Yup from "yup";
import { dateTimeFormat, getLocalStorage } from "../utils";
import { validationValues } from "./validationValues";
import { ROLES } from "../constants";

const EDITOR_MAX_LENGTH = 7500;

export const loginSchema = Yup.object().shape({
  email: Yup.string().label("Email or phone number").required(),
  password: Yup.string().label("Password").trim().required(),
});

export const customerSignUpSchema = Yup.object().shape({
  email: validationValues.EmailAddress,
  password: Yup.string()
    .label("Password")
    .required()
    .matches(
      /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,16}$/,
      "Password must contain 8 characters, one uppercase, one lowercase, one number and one special case character"
    ),
  confirmPassword: Yup.string()
    .label("Confirm password")
    .required()
    .oneOf(
      [Yup.ref("password")],
      "Confirm password value should be the same as password field"
    ),
  full_name: validationValues.FullNameSchema,
  address: Yup.string()
    .label("Address")
    .trim()
    .required("Select Address From Dropdown"),
  state_id: Yup.object().nullable().required("Please select a state"),
  city_id: Yup.object().nullable().required("Please select a city"),
  postal_code: Yup.string()
    .label("Postal Code")
    .required("Postal code is required")
    .matches(/^\d{6}$/, "Postal code must be exactly 6 digits"),
  contact_number: validationValues.ContactNumber,
  role: Yup.number().required("Role is required"),
  seller_type: Yup.mixed().when("role", (role: any, schema: any) => {
    if (role?.[0] === 5) {
      return schema
        .required("Seller type is required")
        .notOneOf([NaN, null, undefined], "Seller type must be valid");
    }
    return schema.notRequired();
  }),
});

export const newPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .label("Password")
    .required()
    .matches(
      /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,16}$/,
      "Password must contain 8 characters, one uppercase, one lowercase, one number and one special case character"
    ),
  confirmPassword: Yup.string()
    .label("Confirm password")
    .required()
    .oneOf(
      [Yup.ref("password")],
      "Confirm password value should be the same as password field"
    ),
});

export const FPSendMailSchema = Yup.object().shape({
  email: validationValues.EmailAddress,
});

export const blogSchema = Yup.object().shape({
  name: Yup.string().label("Title").trim().required("Title is required"),
  // .matches(/^[a-zA-Z0-9 ]*$/, "Special characters are not allowed"),

  permalink: Yup.string()
    .label("Permalink")
    .trim()
    .required("Permalink is required"),
  content: validationValues.TextEditor({
    label: "Content",
    min: 500,
    max: EDITOR_MAX_LENGTH,
    isRequired: false,
  }),
  category_id: Yup.object().required("Please select a category"),
  images: Yup.array()
    .min(1, "Minimum 1 image is required")
    .max(5, "Maximum 5 images are allowed")
    .test(
      "fileType",
      "Only PNG, JPG, JPEG, and WEBP images are allowed",
      (files, context) => {
        if (!files || !Array.isArray(files)) return false;

        const validFiles = files.filter((file) => {
          const image = file?.image;

          // Only validate if it's a File
          return (
            image instanceof File &&
            validationValues.allowedImageFormats.includes(image.type)
          );
        });

        if (validFiles.length !== files.length) {
          return context.createError({
            message:
              "Some files have invalid types (only PNG, JPG, JPEG, and WEBP allowed).",
          });
        }

        return true;
      }
    )
    .test(
      "fileSize",
      `Each uploaded file must be less than ${validationValues.maxFileSize() / (1024 * 1024)}MB`,
      (files, context) => {
        if (!files || !Array.isArray(files)) return false;

        const validFiles = files.filter((file) => {
          const image = file?.image;

          return (
            image instanceof File &&
            image.size <= validationValues.maxFileSize()
          );
        });

        if (validFiles.length !== files.length) {
          return context.createError({
            message: "Some files exceed the maximum allowed size.",
          });
        }

        return true;
      }
    ),
});

export const updateBlogSchema = Yup.object().shape({
  name: Yup.string().label("title").trim().required("Title is required"),
  permalink: Yup.string()
    .label("Permalink")
    .trim()
    .required("Permalink is required"),
  content: validationValues.TextEditor({
    label: "Content",
    min: 500,
    max: EDITOR_MAX_LENGTH,
    isRequired: false,
  }),
  category_id: Yup.object().nullable().required("Please select a category"),
  images: Yup.array()
    .min(1, "Minimum 1 image is required")
    .max(5, "Maximum 5 images are allowed")
    .test(
      "fileType",
      "Only PNG, JPG, JPEG, and WEBP images are allowed",
      (files, context) => {
        if (!files || !Array.isArray(files)) return false;

        const validFiles = files.filter((file) => {
          const image = file?.image;

          // If image is a string (already uploaded), skip validation
          if (typeof image === "string") return true;

          // Only validate if it's a File
          return (
            image instanceof File &&
            validationValues.allowedImageFormats.includes(image.type)
          );
        });

        if (validFiles.length !== files.length) {
          return context.createError({
            message:
              "Some files have invalid types (only PNG, JPG, JPEG, and WEBP allowed).",
          });
        }

        return true;
      }
    )
    .test(
      "fileSize",
      `Each uploaded file must be less than ${validationValues.maxFileSize() / (1024 * 1024)}MB`,
      (files, context) => {
        if (!files || !Array.isArray(files)) return false;

        const validFiles = files.filter((file) => {
          const image = file?.image;

          // Skip validation for already uploaded images (string URLs)
          if (typeof image === "string") return true;

          return (
            image instanceof File &&
            image.size <= validationValues.maxFileSize()
          );
        });

        if (validFiles.length !== files.length) {
          return context.createError({
            message: "Some files exceed the maximum allowed size.",
          });
        }

        return true;
      }
    ),
});

export const enquiryFollowUpSchema = Yup.object().shape({
  follow_up_message: Yup.string()
    .label("Message")
    .trim()
    .required("Message is required"),
});

export const newAgentSchema = Yup.object().shape({
  first_name: Yup.string()
    .label("First Name")
    .trim()
    .required("First name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z\s]*$/, "Special characters are not allowed"),
  last_name: Yup.string()
    .label("Last Name")
    .trim()
    .required("Last name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z]*$/, "Special characters and spaces are not allowed"),
  contact_number: validationValues.ContactNumber,
  email: validationValues.EmailAddress,
  location: Yup.string()
    .label("Location")
    .trim()
    .required("Location is required")
    .min(3, "Minimum 3 characters are required"),
  state_id: Yup.object().nullable().required("Please select a state"),
  city_id: Yup.object().nullable().required("Please select a city"),
});

export const roleSchema = Yup.object().shape({
  name: Yup.string()
    .label("Name")
    .trim()
    .required("Name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),
  modules: Yup.array().required().min(1, "Please select atleast one module"),
});

export const propertyDraftSchema = Yup.object().shape({
  name: Yup.string()
    .label("Property Name")
    .trim()
    .required("Property name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed")
    .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
  slug: Yup.string().label("Slug").trim().required("Slug is required"),
  price: Yup.string().max(10, "Maximum 10 characters are required"),
  contact_person: Yup.string()
    .label("Name")
    .trim()
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),
  contact_number: Yup.string().label("Contact Person").trim(),
  contact_email: Yup.string()
    .label("Email")
    .nullable()
    .notRequired()
    .trim()
    .test("valid-email", "Invalid Email Address", (value) => {
      if (!value) return true; // Allow empty value
      return /^([\w.-]+@([\w-]+\.)+[\w-]{2,4})?$/.test(value);
    }),
  content: Yup.string()
    .label("Content")
    .trim()
    .test("min-length", "Content must be at least 10 characters", (value) => {
      if (!value || value.trim().length === 0) return true; // Skip validation if empty
      return value.trim().length >= 10;
    })
    .max(
      EDITOR_MAX_LENGTH,
      `Content cannot exceed ${EDITOR_MAX_LENGTH} characters`
    ),
  images: Yup.array()
    // .min(5, "Minimum 5 images are required")
    .max(20, "Maximum 20 images are allowed"),
  propertyLocation: Yup.string()
    .label("Property Location")
    .trim()
    .required("Location is required")
    .max(250, "Maximum 250 characters are required"),
  state: Yup.object().nullable().required("Please select a state"),
  city: Yup.object().nullable().required("Please select a city"),
  postal_code: Yup.string()
    .label("Postal Code")
    .optional()
    .matches(/^\d{6}$/, "Postal code must be exactly 6 digits"),
  launch_date: Yup.date().label("Launch Date"),
  interiorAreaTotal: Yup.number()
    .label("Total Area")
    .min(0, "Value must be greater than or  equal to 0"),
  interioAreaLivable: Yup.number()
    .label("Liveable Area")
    .min(0, "Value must be greater than or  equal to 0"),
  no_of_bedrooms: Yup.number()
    .label("Bedrrooms")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  no_of_bathrooms: Yup.number()
    .label("Bathrooms")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  no_of_floors: Yup.number()
    .label("Floors")
    .min(0, "Value must be greater than or  equal to 0")
    .max(100, "Value must be less than 100")
    .nullable()
    .optional(),
  floor_no: Yup.number()
    .label("Floor No.")
    .min(0, "Value must be greater than or  equal to 0")
    .max(100, "Value must be less than 100")
    .test(
      "is-smaller-than-no-of-floors",
      "Floor number should be smaller than No. of Floors",
      function (value) {
        const { no_of_floors } = this.parent;
        if (!value || !no_of_floors) {
          return true; // Skip validation if fields are empty
        }
        return Number(value) <= Number(no_of_floors);
      }
    )
    .nullable()
    .optional(),
  no_of_lifts: Yup.number()
    .label("Lifts")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  additional_rooms: Yup.number()
    .label("Additional Rooms")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  two_wheeler_parking: Yup.number()
    .label("Two Wheeler Parking")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  four_wheeler_parking: Yup.number()
    .label("Four Wheeler Parking")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  property_status: Yup.string().label("Project status"),
  factFeatures: Yup.array().of(
    Yup.object()
      .shape({
        fact_id: Yup.object().nullable(),
        fact_description: Yup.string().trim(),
      })
      .optional()
  ),
  facilities: Yup.array().of(
    Yup.object().shape({
      facility_id: Yup.object().nullable(),
      nearby_area: Yup.string()
        .trim()
        .min(3, "Minimum 3 characters are required"),
      distance: Yup.number()
        .min(0, "Distance must be at least 0")
        .max(15, "Distance must be 15 or less"),
    })
  ),
  tax: Yup.array()
    .of(
      Yup.object().shape({
        tax_year: Yup.string(),
        status: Yup.object().nullable(),
      })
    )
    .optional()
    .default([]),
  category_id: Yup.object().nullable(),
});

export const propertySaveSchema = Yup.object().shape({
  name: Yup.string()
    .label("Property Name")
    .trim()
    .required("Property name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed")
    .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
  slug: Yup.string().label("Slug").trim().required("Slug is required"),
  price: Yup.string()
    .required("Price is required")
    .max(10, "Maximum 10 characters are required"),
  contact_person: Yup.string()
    .label("Name")
    .trim()
    .required("Name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),
  contact_number: validationValues.ContactNumber,
  contact_email: validationValues.EmailAddress,
  content: validationValues.TextEditor({
    label: "Content",
    min: 10,
    max: EDITOR_MAX_LENGTH,
    isRequired: false,
  }),
  images: Yup.array()
    .required("Image is required")
    .min(5, "Minimum 5 images are required")
    .max(20, "Maximum 20 images are allowed"),
  propertyLocation: Yup.string()
    .label("Property Location")
    .trim()
    .required("Location is required")
    .max(250, "Maximum 250 characters are required"),
  state: Yup.object().nullable().required("Please select a state"),
  city: Yup.object().nullable().required("Please select a city"),
  postal_code: Yup.string()
    .label("Postal Code")
    .optional()
    .matches(/^\d{6}$/, "Postal code must be exactly 6 digits"),
  launch_date: Yup.date().label("Launch Date").required(),
  interiorAreaTotal: Yup.number()
    .label("Total Area")
    .required("Total area is required")
    .min(0, "Value must be greater than or  equal to 0"),
  interioAreaLivable: Yup.number()
    .label("Liveable Area")
    .required("Liveable area is required")
    .min(0, "Value must be greater than or  equal to 0"),
  no_of_bedrooms: Yup.number()
    .label("Bedrrooms")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  no_of_bathrooms: Yup.number()
    .label("Bathrooms")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  no_of_floors: Yup.number()
    .label("Floors")
    .min(0, "Value must be greater than or  equal to 0")
    .max(100, "Value must be less than 100")
    .nullable()
    .optional(),
  floor_no: Yup.number()
    .label("Floor No.")
    .min(0, "Value must be greater than or  equal to 0")
    .max(100, "Value must be less than 100")
    .test(
      "is-smaller-than-no-of-floors",
      "Floor number should be smaller than No. of Floors",
      function (value) {
        const { no_of_floors } = this.parent;
        if (!value || !no_of_floors) {
          return true; // Skip validation if fields are empty
        }
        return Number(value) <= Number(no_of_floors);
      }
    )
    .nullable()
    .optional(),
  no_of_lifts: Yup.number()
    .label("Lifts")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  additional_rooms: Yup.number()
    .label("Additional Rooms")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  two_wheeler_parking: Yup.number()
    .label("Two Wheeler Parking")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  four_wheeler_parking: Yup.number()
    .label("Four Wheeler Parking")
    .min(0, "Value must be greater than or  equal to 0")
    .max(20, "Value must be less than 20")
    .nullable()
    .optional(),
  property_status: Yup.string()
    .label("Project status")
    .required("Property status is required"),
  factFeatures: Yup.array().of(
    Yup.object()
      .shape({
        fact_id: Yup.object().nullable().required("Please select a fact"),
        fact_description: Yup.string()
          .trim()
          .required("Description is required"),
      })
      .optional()
  ),
  facilities: Yup.array().of(
    Yup.object().shape({
      facility_id: Yup.object().nullable().required("Please select a facility"),
      nearby_area: Yup.string()
        .trim()
        .min(3, "Minimum 3 characters are required")
        .required("Required"),
      distance: Yup.number()
        .required("Distance is required")
        .min(0, "Distance must be at least 0")
        .max(15, "Distance must be 15 or less"),
      duration: Yup.string().required("Duration is required").trim(),
    })
  ),
  tax: Yup.array()
    .of(
      Yup.object().shape({
        tax_year: Yup.string().required("Required"),
        // assessment: Yup.string().required("Required"),
        status: Yup.object().nullable().required("Required"),
      })
    )
    .optional()
    .default([]),
  category_id: Yup.object().nullable().required("Please select a category"),
});

export const projectSaveSchema = Yup.object().shape({
  name: Yup.string()
    .label("Project Name")
    .trim()
    .required("Project name is required")
    .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
  slug: Yup.string().label("Slug").trim().required("Slug is required"),
  builders: Yup.array()
    .of(Yup.object().nullable().required("Please select a builder"))
    .test("builder-select", "Please Select a builder", (value: any) => {
      return value?.length || getLocalStorage("userRole") == ROLES.BUILDER;
    })
    .test("max-builder", "Maximum 5 Builders are allowed", (value: any) => {
      return value?.length <= 5;
    }),
  contact_number: Yup.number()
    .label("Phone")
    .typeError("Phone number must be a valid number")
    .required("Phone number is required")
    .test(
      "valid-phone",
      "Phone number must start with 6, 7, 8, or 9 and must be exactly 10 digits",
      (value: any) => {
        const phoneNumber = value?.toString();
        return phoneNumber && /^[6-9]\d{9}$/.test(phoneNumber);
      }
    ),
  contact_person: Yup.string()
    .label("Contact Person")
    .trim()
    .required("Contact person is required"),
  contact_email: validationValues.EmailAddress,
  content: validationValues.TextEditor({
    label: "Content",
    min: 10,
    max: EDITOR_MAX_LENGTH,
    isRequired: false,
  }),
  images: Yup.array()
    .required("Image is required")
    .min(5, "Minimum 5 images are required")
    .max(20, "Maximum 20 images are allowed"),
  // .test("fileType", "Only image files are allowed", (files) =>
  //   files?.every((file) => file.type.startsWith("image/"))
  // ),
  project_area: Yup.string()
    .label("Project Area")
    .trim()
    .required("Project area is required")
    .test("project-area", "Only positive numbers are allowed", (value: any) => {
      return value > 0;
    }),
  project_location: Yup.string()
    .label("Project Location")
    .trim()
    .required("Location is required")
    .max(250, "Maximum 250 characters are required"),
  lat: Yup.string().label("Latitude").trim().required("Latitude is required"),
  long: Yup.string()
    .label("Longitude")
    .trim()
    .required("Longitude is required"),
  state: Yup.object().nullable().required("Please select a state"),
  city: Yup.object().nullable().required("Please select a city"),
  postal_code: Yup.string()
    .label("Postal Code")
    .optional()
    .matches(/^\d{6}$/, "Postal code must be exactly 6 digits"),
  launch_date: Yup.date().label("Launch Date").required(),
  project_type: Yup.string()
    .label("Project Type")
    .required("Project type is required"),
  rera_number: Yup.string()
    .label("Rera Number")
    .trim()
    .required("Rera number is required")
    .max(100, "Maximum 100 characters are required"),
  factFeatures: Yup.array().of(
    Yup.object()
      .shape({
        fact_id: Yup.object().nullable().required("Please select a fact"),
        fact_description: Yup.string()
          .trim()
          .required("Description is required"),
      })
      .optional()
  ),
  facilities: Yup.array().of(
    Yup.object().shape({
      facility_id: Yup.object().nullable().required("Please select a facility"),
      nearby_area: Yup.string()
        .min(3, "Minimum 3 characters are required")
        .trim()
        .required("Required"),
      distance: Yup.number()
        .required("Distance is required")
        .min(0, "Distance must be at least 0")
        .max(15, "Distance must be 15 or less"),
      duration: Yup.string().required("Duration is required").trim(),
    })
  ),
  building: Yup.array().of(
    Yup.object().shape({
      building_wing: Yup.string(),
      building_type: Yup.object().required("Building type is required"),
      building_interior_area: Yup.string().required(
        "Building interior area is required"
      ),
      building_outer_area: Yup.string().when(
        "building_type.value",
        (value, schema) => {
          if (value[0] !== 0) {
            return schema
              .required("Building outer area is required")
              .test(
                "is-greater-than-interior",
                "Outer Exceeds Interior",
                function (outerAreaValue) {
                  const { building_interior_area } = this.parent;
                  if (!outerAreaValue || !building_interior_area) {
                    return true; // Skip validation if fields are empty
                  }
                  return (
                    Number(outerAreaValue) >= Number(building_interior_area)
                  );
                }
              );
          }
          return schema; // Skip validation for "commercial"
        }
      ),
      building_expected_price: Yup.string().required(
        "Building expected price is required"
      ),
      building_units: Yup.string().required("Building units is required"),
      building_floor_plan: Yup.array()
        .required("Building floor plan is required")
        .min(1, "Minimum 1 images")
        .max(3, "Maximum 3 images"),
    })
  ),
  project_brochure: Yup.mixed().required("Brochure is required"),
  category_id: Yup.object().nullable().required("Please select a category"),
});

export const projectDraftSchema = Yup.object().shape({
  name: Yup.string()
    .label("Project Name")
    .trim()
    .required("Project name is required")
    .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
  slug: Yup.string().label("Slug").trim().required("Slug is required"),
  builders: Yup.array()
    .of(Yup.object().nullable())
    .test("max-builder", "Maximum 5 Builders are allowed", (value: any) => {
      return !value || value.length <= 5; // Validate only if the array has values
    })
    .test("builder-select", "Please Select a builder", (value: any) => {
      return (
        !value ||
        value.length >= 0 ||
        getLocalStorage("userRole") === ROLES.BUILDER
      );
    }),
  contact_number: Yup.string()
    .label("Phone")
    .nullable()
    .notRequired()
    .test(
      "valid-phone",
      "Phone number must start with 6, 7, 8, or 9 and must be exactly 10 digits",
      (value) => {
        if (!value) return true;
        return /^[6-9]\d{9}$/.test(value.toString());
      }
    ),
  contact_person: Yup.string().label("Contact Person").trim(),
  contact_email: Yup.string()
    .label("Email")
    .nullable()
    .notRequired()
    .trim()
    .test("valid-email", "Invalid Email Address", (value) => {
      if (!value) return true; // Allow empty value
      return /^([\w.-]+@([\w-]+\.)+[\w-]{2,4})?$/.test(value);
    }),
  content: Yup.string()
    .label("Content")
    .trim()
    .test("min-length", "Content must be at least 10 characters", (value) => {
      if (!value || value.trim().length === 0) return true; // Skip validation if empty
      return value.trim().length >= 10;
    })
    .max(
      EDITOR_MAX_LENGTH,
      `Content cannot exceed ${EDITOR_MAX_LENGTH} characters`
    ),
  images: Yup.array()
    .test("min-images", "Minimum 5 images are required", (value) => {
      if (!value || value.length === 0) return true; // Skip validation if empty
      return value.length >= 5;
    })
    .max(20, "Maximum 20 images are allowed"),
  project_area: Yup.string()
    .label("Project Area")
    .trim()
    .test("project-area", "Only positive numbers are allowed", (value: any) => {
      if (!value) return true;
      return /^[+]?\d+(\.\d+)?$/.test(value);
    }),
  project_location: Yup.string()
    .label("Project Location")
    .trim()
    .required("Location is required")
    .max(250, "Maximum 250 characters are required"),
  lat: Yup.string().label("Latitude").trim().required("Latitude is required"),
  long: Yup.string()
    .label("Longitude")
    .trim()
    .required("Longitude is required"),
  state: Yup.object().nullable().required("Please select a state"),
  city: Yup.object().nullable().required("Please select a city"),
  postal_code: Yup.string()
    .label("Postal Code")
    .optional()
    .matches(/^\d{6}$/, "Postal code must be exactly 6 digits"),
  launch_date: Yup.date().label("Launch Date"),
  project_type: Yup.string().label("Project Type"),
  rera_number: Yup.string()
    .label("Rera Number")
    .trim()
    .max(100, "Maximum 100 characters are required"),
  factFeatures: Yup.array().of(
    Yup.object()
      .shape({
        fact_id: Yup.object().nullable(),
        fact_description: Yup.string().trim(),
      })
      .optional()
  ),
  facilities: Yup.array().of(
    Yup.object().shape({
      facility_id: Yup.object().nullable(),
      nearby_area: Yup.string()
        .min(3, "Minimum 3 characters are required")
        .trim(),
      distance: Yup.number()
        .min(0, "Distance must be at least 0")
        .max(15, "Distance must be 15 or less"),
    })
  ),
  building: Yup.array().of(
    Yup.object().shape({
      building_wing: Yup.string(),
      building_type: Yup.object(),
      building_interior_area: Yup.string(),
      building_outer_area: Yup.string().when(
        "building_type.value",
        (value, schema) => {
          if (value && value[0] !== 0) {
            return schema.test(
              "is-greater-than-interior",
              "Outer Exceeds Interior",
              function (outerAreaValue) {
                const { building_interior_area } = this.parent;
                if (!outerAreaValue || !building_interior_area) {
                  return true; // Skip validation if fields are empty
                }
                return Number(outerAreaValue) >= Number(building_interior_area);
              }
            );
          }
          return schema; // Skip validation for "commercial"
        }
      ),
      building_expected_price: Yup.string(),
      building_units: Yup.string(),
      // building_floor_plan: Yup.mixed(),
    })
  ),
  category_id: Yup.object().nullable(),
});

export const BuilderSchema = Yup.object({
  first_name: Yup.string()
    .label("First Name")
    .trim()
    .required("First name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z\s]*$/, "Special characters are not allowed"),
  last_name: Yup.string()
    .label("Last Name")
    .trim()
    .required("Last name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z]*$/, "Special characters and spaces are not allowed"),
  email: validationValues.EmailAddress,
  location: Yup.string()
    .label("Location")
    .trim()
    .required("Location is required")
    .min(3, "Minimum 3 characters are required"),
  contact_number: validationValues.ContactNumber,
  state: Yup.object().required("State is required"),
  city: Yup.object().required("City is required"),
  postal_code: Yup.string()
    .label("Postal Code")
    .required("Postal code is required")
    .matches(/^\d{6}$/, "Postal code must be exactly 6 digits"),
});

export const TermsOfServiceValidator = Yup.object({
  name: Yup.string()
    .label("name")
    .trim()
    .required("name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),

  description: validationValues.TextEditor({
    label: "description",
    min: 10,
    max: EDITOR_MAX_LENGTH,
    isRequired: false,
  }),
});

export const AboutServiceValidator = Yup.object({
  name: Yup.string()
    .label("name")
    .trim()
    .required("name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),

  description: validationValues.TextEditor({
    label: "description",
    min: 10,
    max: EDITOR_MAX_LENGTH,
    isRequired: false,
  }),
});

export const PrivacyPolicyServiceValidator = Yup.object({
  name: Yup.string()
    .label("name")
    .trim()
    .required("name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),

  description: validationValues.TextEditor({
    label: "description",
    min: 10,
    max: EDITOR_MAX_LENGTH + 5000,
    isRequired: false,
  }),
});

export const AmenitiesSchema = Yup.object({
  name: Yup.string()
    .label("name")
    .trim()
    .required("Name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed")
    // .matches(/^[a-zA-Z\s]*$/, "Amenities name accept only letters")
    .notOneOf(
      ["Amenities1", "Amenities2", "Amenities3"],
      "This name is not allowed"
    ),
  icon: Yup.mixed().required("icon is required"),
});

export const CategorySchema = Yup.object({
  name: Yup.string()
    .label("name")
    .trim()
    .required("Name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed")
    // .matches(/^[a-zA-Z\s]*$/, "category name accept only letters")
    .notOneOf(
      ["category1", "category2", "category3"],
      "This name is not allowed"
    ),
  description: Yup.string()
    .label("description")
    .trim()
    .min(5, "Minimum 5 characters are required"),
  image: Yup.mixed().required("Image is required"),
});

export const subCategorySchema = Yup.object({
  category_id: Yup.object().label("category").required("Category is required"),
  name: Yup.string()
    .trim()
    .label("sub category")
    .required("Sub Category is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed")
    .notOneOf(
      ["subcategory1", "subcategory2", "subcategory3"],
      "This name is not allowed"
    ),
});

export const ImageCategorySchema = Yup.object({
  name: Yup.string()
    .label("name")
    .trim()
    .required("Name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed")
    // .matches(/^[a-zA-Z\s]*$/, "category name accept only letters")
    .notOneOf(
      ["category1", "category2", "category3"],
      "This name is not allowed"
    ),
});

export const AppliancesSchema = Yup.object({
  name: Yup.string()
    .label("name")
    .trim()
    .required("name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),

  descrition: Yup.string()
    .label("description")
    .trim()
    .min(5, "Minimum 5 characters are required"),
});

export const ContactUsSchema = Yup.object({
  replyMessage: Yup.string()
    .label("reply message")
    .trim()
    .required("reply message is required")
    .min(5, "Minimum 5 characters are required"),
});

export const FactFeaturesSchema = Yup.object({
  name: Yup.string()
    .label("fact feature name")
    .trim()
    .required("fact feature name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),
  // .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
});

export const faqsSchema = Yup.object({
  category_id: Yup.object().nullable().required("Please select a category"),

  question: Yup.string()
    .label("question")
    .trim()
    .required("Question is required")
    .min(3, "Minimum 3 characters are required"),

  answer: validationValues.TextEditor({
    label: "answer",
    min: 10,
    max: EDITOR_MAX_LENGTH,
    isRequired: false,
  }),
});

export const changePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .label("current password")
    .required("Current password is required"),
  newPassword: Yup.string()
    .label("Password")
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .max(15, "Password must be at most 15 characters")
    .matches(
      /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,15}$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, one special character, and no spaces"
    ),
  confirmPassword: Yup.string()
    .label("Confirm password")
    .required()
    .oneOf(
      [Yup.ref("newPassword")],
      "Confirm password value should be the same as password field"
    ),
});

export const allStaffSchema = Yup.object({
  first_name: Yup.string()
    .label("First Name")
    .trim()
    .required("First name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z\s]*$/, "Special characters are not allowed"),
  last_name: Yup.string()
    .label("Last Name")
    .trim()
    .required("Last name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z]*$/, "Special characters and spaces are not allowed"),
  email: validationValues.EmailAddress,
  address: Yup.string()
    .label("Address")
    .trim()
    .required("Address is required")
    .min(3, "Minimum 3 characters are required"),
  contact_number: validationValues.ContactNumber,
  state_id: Yup.object().required("State is required"),
  city_id: Yup.object().required("City is required"),
  location: Yup.string()
    .label("Property Location")
    .trim()
    .required("Location is required")
    .max(250, "Maximum 250 characters are required"),
  role_id: Yup.object().required("select your roles "),
});

export const ServicesAvailabilityFormSchema = Yup.object({
  name: Yup.string()
    .label("name")
    .trim()
    .required()
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are required"),
  email: validationValues.EmailAddress,
  contact_number: validationValues.ContactNumber,
  message: Yup.string()
    .min(5, "5 characters are required")
    .max(250, "250 characters are allowed")
    .optional(),
});

export const enquirySchema = Yup.object({
  full_name: Yup.string().required("Full name required"),
  email: validationValues.EmailAddress,
  contact_number: validationValues.ContactNumber,
  budget: Yup.number()
    .label("Budget")
    .required("Budget is required")
    .min(1, "Budget cannot be 0") // Ensures value is at least 1
    .max(99999, "Maximum 5 digits are allowed") // Max 5 digits
    .typeError("Budget must be a number"), // Handles invalid inputs
  interested_in_home_loan: Yup.boolean().optional().default(false),
  agree_to_receive_message_call: Yup.boolean().optional().default(false),
  message: Yup.object().label("Message").required(),
  other_message: Yup.string()
    .optional()
    .test("not empty", "Other message is required", function (value, context) {
      if (context?.parent?.message?.value?.toLocaleLowerCase() === "other") {
        return !!value?.trim();
      }

      return true; // If `checked` is false, skip this validation
    }),
});

export const feedbackSchema = Yup.object({
  subject: Yup.string()
    .label("Subject")
    .trim()
    .required()
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are required"),
  feedback: Yup.string()
    .label("Feedback")
    .trim()
    .required()
    .min(3, "Minimum 3 characters are required")
    .max(500, "Maximum 500 characters are allowed"),
});

export const meetingSchema = Yup.object({
  message: Yup.object().label("Message").required(),
  other_message: Yup.string()
    .optional()
    .test("not empty", "Other message is required", function (value, context) {
      if (context?.parent?.message?.value?.toLocaleLowerCase() === "other") {
        return !!value?.trim();
      }

      return true; // If `checked` is false, skip this validation
    }),
});

export const multiplePropertyFormSchema = [
  Yup.object().shape({
    name: Yup.string()
      .label("Property Name")
      .trim()
      .required("Property name is required")
      .min(3, "Minimum 3 characters are required")
      .max(50, "Maximum 50 characters are allowed")
      .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
    category_id: Yup.object().nullable().required("Please select a category"),
    propertyLocation: Yup.string()
      .label("Property Location")
      .trim()
      .required("Location is required")
      .max(250, "Maximum 250 characters are required"),
    state_id: Yup.object().nullable().required("Please select a state"),
    city_id: Yup.object().nullable().required("Please select a city"),
    postal_code: Yup.string()
      .label("Postal Code")
      .optional()
      .matches(/^\d{6}$/, "Postal code must be exactly 6 digits"),
    lat: Yup.string().label("Latitude").trim().required("Latitude is required"),
    long: Yup.string()
      .label("Longitude")
      .trim()
      .required("Longitude is required"),
  }),
  Yup.object().shape({
    contact_person: Yup.string()
      .label("Name")
      .trim()
      .required("Name is required")
      .min(3, "Minimum 3 characters are required")
      .max(50, "Maximum 50 characters are allowed"),
    contact_number: validationValues.ContactNumber,
    contact_email: validationValues.EmailAddress,
    images: Yup.array()
      .required("Image is required")
      .min(5, "Minimum 5 images are required")
      .max(20, "Maximum 20 images are allowed"),
  }),
  Yup.object().shape({
    no_of_bedrooms: Yup.number()
      .label("Bedrrooms")
      .min(0, "Value must be greater than or  equal to 0")
      .nullable()
      .max(20, "Value must be less than 20")
      .optional(),
    no_of_bathrooms: Yup.number()
      .label("Bathrooms")
      .min(0, "Value must be greater than or  equal to 0")
      .nullable()
      .max(20, "Value must be less than 20")
      .optional(),
    no_of_floors: Yup.number()
      .label("Floors")
      .min(0, "Value must be greater than or  equal to 0")
      .nullable()
      .max(100, "Value must be less than 100")
      .optional(),
    floor_no: Yup.number()
      .label("Floor No.")
      .min(0, "Value must be greater than or  equal to 0")
      .nullable()
      .max(100, "Value must be less than 100")
      .test(
        "is-smaller-than-no-of-floors",
        "Floor number should be smaller than No. of Floors",
        function (value) {
          const { no_of_floors } = this.parent;
          if (!value || !no_of_floors) {
            return true; // Skip validation if fields are empty
          }
          return Number(value) <= Number(no_of_floors);
        }
      )
      .optional(),
    no_of_lifts: Yup.number()
      .label("Lifts")
      .min(0, "Value must be greater than or  equal to 0")
      .nullable()
      .max(20, "Value must be less than 20")
      .optional(),
    content: validationValues.TextEditor({
      label: "Content",
      min: 10,
      max: EDITOR_MAX_LENGTH,
      isRequired: false,
    }),
    additional_rooms: Yup.number()
      .label("Additional Rooms")
      .min(0, "Value must be greater than or  equal to 0")
      .max(20, "Value must be less than 20")
      .nullable()
      .optional(),
    interiorAreaTotal: Yup.number()
      .label("Total Area")
      .required("Total area is required")
      .min(0, "Value must be greater than or  equal to 0"),
    interioAreaLivable: Yup.number()
      .label("Liveable Area")
      .required("Liveable area is required")
      .min(0, "Value must be greater than or  equal to 0"),
    two_wheeler_parking: Yup.number()
      .label("Two Wheeler Parking")
      .min(0, "Value must be greater than or  equal to 0")
      .max(20, "Value must be less than 20")
      .nullable()
      .optional(),
    four_wheeler_parking: Yup.number()
      .label("Four Wheeler Parking")
      .min(0, "Value must be greater than or  equal to 0")
      .max(20, "Value must be less than 20")
      .nullable()
      .optional(),
  }),
  Yup.object().shape({
    price: Yup.string()
      .required("Price is required")
      .max(10, "Maximum 10 characters are required"),
    factFeatures: Yup.array().of(
      Yup.object()
        .shape({
          fact_id: Yup.object().nullable().required("Please select a fact"),
          fact_description: Yup.string().required("Description is required"),
        })
        .optional()
    ),
    facilities: Yup.array().of(
      Yup.object().shape({
        facility_id: Yup.object()
          .nullable()
          .required("Please select a facility"),
        nearby_area: Yup.string()
          .min(3, "Minimum 3 characters are required")
          .required("Required"),
        distance: Yup.number()
          .required("Distance is required")
          .min(0, "Distance must be at least 0")
          .max(15, "Distance must be 15 or less"),
        duration: Yup.string().required("Duration is required").trim(),
      })
    ),
    launch_date: Yup.date().label("Launch Date").required(),
    property_status: Yup.string()
      .label("Project status")
      .required("Property status is required"),
  }),
  Yup.object().shape({
    tax: Yup.array()
      .of(
        Yup.object().shape({
          tax_year: Yup.string().required("Required"),
          // assessment: Yup.string().required("Required"),
          status: Yup.object().nullable().required("Required"),
        })
      )
      .optional()
      .default([]),
  }),
];

export const cancelMeetingSchema = Yup.object({
  reason: Yup.string()
    .label("Reason")
    .trim()
    .required()
    .min(3, "Minimum 3 characters are required")
    .max(255, "Maximum 255 characters are required"),
});

export const completeMeetingSchema = Yup.object({
  conclusion: Yup.string()
    .label("Meeting conclusion")
    .trim()
    .required()
    .min(3, "Minimum 3 characters are required")
    .max(255, "Maximum 255 characters are required"),
  interest_status: Yup.object()
    .nullable()
    .required("Please select a interest status"),
});

export const meetingAvailabilitySchema = Yup.object().shape({
  availability: Yup.object().shape({
    sunday: Yup.object().shape({
      start: Yup.string()
        .nullable()
        .test("not empty", "Start time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test(
          "start_time_test",
          "Start time must be before end time",
          function (value) {
            const { end, checked } = this.parent;
            if (checked) {
              return moment(value).isBefore(moment(end));
            }
            return true; // If `checked` is false, skip this validation
          }
        )
        .test(
          "valid_start_time",
          "Invalid start time format",
          function (value) {
            const { checked } = this.parent;
            if (checked) {
              const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
              const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
              return timeRegex.test(time); // Validate the time string
            }
            return true; // If `checked` is false, skip this validation
          }
        ),
      end: Yup.string()
        .nullable()
        .test("not empty", "End time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test("valid_end_time", "Invalid end time format", function (value) {
          const { checked } = this.parent;
          if (checked) {
            const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
            const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
            return timeRegex.test(time); // Validate the time string
          }
          return true; // If `checked` is false, skip this validation
        }),
    }),
    monday: Yup.object().shape({
      start: Yup.string()
        .nullable()
        .test("not empty", "Start time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test(
          "start_time_test",
          "Start time must be before end time",
          function (value) {
            const { end, checked } = this.parent;
            if (checked) {
              return moment(value).isBefore(moment(end));
            }
            return true; // If `checked` is false, skip this validation
          }
        )
        .test(
          "valid_start_time",
          "Invalid start time format",
          function (value) {
            const { checked } = this.parent;
            if (checked) {
              const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
              const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
              return timeRegex.test(time); // Validate the time string
            }
            return true; // If `checked` is false, skip this validation
          }
        ),
      end: Yup.string()
        .nullable()
        .test("not empty", "End time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test("valid_end_time", "Invalid end time format", function (value) {
          const { checked } = this.parent;
          if (checked) {
            const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
            const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
            return timeRegex.test(time); // Validate the time string
          }
          return true; // If `checked` is false, skip this validation
        }),
    }),
    tuesday: Yup.object().shape({
      start: Yup.string()
        .nullable()
        .test("not empty", "Start time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test(
          "start_time_test",
          "Start time must be before end time",
          function (value) {
            const { end, checked } = this.parent;
            if (checked) {
              return moment(value).isBefore(moment(end));
            }
            return true; // If `checked` is false, skip this validation
          }
        )
        .test(
          "valid_start_time",
          "Invalid start time format",
          function (value) {
            const { checked } = this.parent;
            if (checked) {
              const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
              const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
              return timeRegex.test(time); // Validate the time string
            }
            return true; // If `checked` is false, skip this validation
          }
        ),
      end: Yup.string()
        .nullable()
        .test("not empty", "End time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test("valid_end_time", "Invalid end time format", function (value) {
          const { checked } = this.parent;
          if (checked) {
            const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
            const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
            return timeRegex.test(time); // Validate the time string
          }
          return true; // If `checked` is false, skip this validation
        }),
    }),
    wednesday: Yup.object().shape({
      start: Yup.string()
        .nullable()
        .test("not empty", "Start time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test(
          "start_time_test",
          "Start time must be before end time",
          function (value) {
            const { end, checked } = this.parent;
            if (checked) {
              return moment(value).isBefore(moment(end));
            }
            return true; // If `checked` is false, skip this validation
          }
        )
        .test(
          "valid_start_time",
          "Invalid start time format",
          function (value) {
            const { checked } = this.parent;
            if (checked) {
              const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
              const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
              return timeRegex.test(time); // Validate the time string
            }
            return true; // If `checked` is false, skip this validation
          }
        ),
      end: Yup.string()
        .nullable()
        .test("not empty", "End time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test("valid_end_time", "Invalid end time format", function (value) {
          const { checked } = this.parent;
          if (checked) {
            const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
            const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
            return timeRegex.test(time); // Validate the time string
          }
          return true; // If `checked` is false, skip this validation
        }),
    }),
    thursday: Yup.object().shape({
      start: Yup.string()
        .nullable()
        .test("not empty", "Start time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test(
          "start_time_test",
          "Start time must be before end time",
          function (value) {
            const { end, checked } = this.parent;
            if (checked) {
              return moment(value).isBefore(moment(end));
            }
            return true; // If `checked` is false, skip this validation
          }
        )
        .test(
          "valid_start_time",
          "Invalid start time format",
          function (value) {
            const { checked } = this.parent;
            if (checked) {
              const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
              const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
              return timeRegex.test(time); // Validate the time string
            }
            return true; // If `checked` is false, skip this validation
          }
        ),
      end: Yup.string()
        .nullable()
        .test("not empty", "End time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test("valid_end_time", "Invalid end time format", function (value) {
          const { checked } = this.parent;
          if (checked) {
            const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
            const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
            return timeRegex.test(time); // Validate the time string
          }
          return true; // If `checked` is false, skip this validation
        }),
    }),
    friday: Yup.object().shape({
      start: Yup.string()
        .nullable()
        .test("not empty", "Start time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test(
          "start_time_test",
          "Start time must be before end time",
          function (value) {
            const { end, checked } = this.parent;
            if (checked) {
              return moment(value).isBefore(moment(end));
            }
            return true; // If `checked` is false, skip this validation
          }
        )
        .test(
          "valid_start_time",
          "Invalid start time format",
          function (value) {
            const { checked } = this.parent;
            if (checked) {
              const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
              const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
              return timeRegex.test(time); // Validate the time string
            }
            return true; // If `checked` is false, skip this validation
          }
        ),
      end: Yup.string()
        .nullable()
        .test("not empty", "End time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test("valid_end_time", "Invalid end time format", function (value) {
          const { checked } = this.parent;
          if (checked) {
            const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
            const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
            return timeRegex.test(time); // Validate the time string
          }
          return true; // If `checked` is false, skip this validation
        }),
    }),
    saturday: Yup.object().shape({
      start: Yup.string()
        .nullable()
        .test("not empty", "Start time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test(
          "start_time_test",
          "Start time must be before end time",
          function (value) {
            const { end, checked } = this.parent;
            if (checked) {
              return moment(value).isBefore(moment(end));
            }
            return true; // If `checked` is false, skip this validation
          }
        )
        .test(
          "valid_start_time",
          "Invalid start time format",
          function (value) {
            const { checked } = this.parent;
            if (checked) {
              const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
              const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
              return timeRegex.test(time); // Validate the time string
            }
            return true; // If `checked` is false, skip this validation
          }
        ),
      end: Yup.string()
        .nullable()
        .test("not empty", "End time can't be empty", function (value) {
          const { checked } = this.parent;
          if (checked) {
            return !!value;
          }
          return true; // If `checked` is false, skip this validation
        })
        .test("valid_end_time", "Invalid end time format", function (value) {
          const { checked } = this.parent;
          if (checked) {
            const time = dateTimeFormat(String(value), "hh:mm"); // Convert date to "HH:mm"
            const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
            return timeRegex.test(time); // Validate the time string
          }
          return true; // If `checked` is false, skip this validation
        }),
    }),
  }),
});

export const updateUnavailableDateSchema = Yup.object({
  date: Yup.date().label("Date").required(),
});

export const multipleProjectFormSchema = [
  Yup.object().shape({
    name: Yup.string()
      .label("Project Name")
      .trim()
      .required("Project name is required")
      .min(3, "Minimum 3 characters are required")
      .max(50, "Maximum 50 characters are allowed")
      .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
    category_id: Yup.object().nullable().required("Please select a category"),
    project_location: Yup.string()
      .label("Project Location")
      .trim()
      .required("Location is required")
      .max(250, "Maximum 250 characters are required"),
    state_id: Yup.object().nullable().required("Please select a state"),
    city_id: Yup.object().nullable().required("Please select a city"),
    postal_code: Yup.string()
      .label("Postal Code")
      .optional()
      .matches(/^\d{6}$/, "Postal code must be exactly 6 digits"),
    lat: Yup.string().label("Latitude").trim().required("Latitude is required"),
    long: Yup.string()
      .label("Longitude")
      .trim()
      .required("Longitude is required"),
  }),
  Yup.object().shape({
    contact_person: Yup.string()
      .label("Name")
      .trim()
      .required("Name is required")
      .min(3, "Minimum 3 characters are required")
      .max(50, "Maximum 50 characters are allowed"),
    contact_number: validationValues.ContactNumber,
    contact_email: validationValues.EmailAddress,
    project_brochure: Yup.mixed().required("Brochure is required"),
    images: Yup.array()
      .required("Image is required")
      .min(5, "Minimum 5 images are required")
      .max(20, "Maximum 20 images are allowed"),
  }),
  Yup.object().shape({
    launch_date: Yup.date().label("Launch Date").required(),
    content: validationValues.TextEditor({
      label: "Content",
      min: 10,
      max: EDITOR_MAX_LENGTH,
      isRequired: false,
    }),
    factFeatures: Yup.array()
      .of(
        Yup.object().shape({
          fact_id: Yup.object().nullable().required("Please select a fact"),
          fact_description: Yup.string().required("Description is required"),
        })
      )
      .optional(),
    facilities: Yup.array().of(
      Yup.object().shape({
        facility_id: Yup.object()
          .nullable()
          .required("Please select a facility"),
        nearby_area: Yup.string()
          .min(3, "Minimum 3 characters are required")
          .trim()
          .required("Required"),
        distance: Yup.number()
          .required("Distance is required")
          .min(0, "Distance must be at least 0")
          .max(15, "Distance must be 15 or less"),
        duration: Yup.string().required("Duration is required").trim(),
      })
    ),
    project_area: Yup.string()
      .label("Project Area")
      .trim()
      .required("Project area is required")
      .test(
        "project-area",
        "Only positive numbers are allowed",
        (value: any) => {
          return value > 0;
        }
      ),
    project_type: Yup.string()
      .label("Project Type")
      .required("Project type is required"),
    project_status: Yup.string()
      .label("Project status")
      .required("Property status is required"),
    rera_number: Yup.string()
      .label("Rera Number")
      .trim()
      .required("Rera number is required")
      .max(100, "Maximum 100 characters are required"),
  }),
  Yup.object().shape({
    building: Yup.array().of(
      Yup.object().shape({
        building_type: Yup.object().nullable().required("Required"),
        building_interior_area: Yup.string().required("Required"),
        building_outer_area: Yup.string().when(
          "building_type.value",
          (value, schema) => {
            if (value[0] !== 0) {
              return schema
                .required("Required")
                .test(
                  "is-greater-than-interior",
                  "Outer Exceeds Interior",
                  function (outerAreaValue) {
                    const { building_interior_area } = this.parent;
                    if (!outerAreaValue || !building_interior_area) {
                      return true; // Skip validation if fields are empty
                    }
                    return (
                      Number(outerAreaValue) >= Number(building_interior_area)
                    );
                  }
                );
            }
            return schema; // Skip validation for "commercial"
          }
        ),
        building_expected_price: Yup.string().required("Required"),
        building_floor_plan: Yup.array()
          .required("Plan is required")
          .min(1, "Minimum 1 images")
          .max(3, "Maximum 3 images"),
      })
    ),
  }),
];

export const sliderImageSchema = Yup.object({
  ref_id: Yup.string().required("Required"),
  type: Yup.object().required("Required"),
  slider: Yup.array().min(1, "Select banner image").required("Required"),
});

export const subscribeSchema = Yup.object({
  subscriber_email: validationValues.EmailAddress,
});
export const notificationSchema = Yup.object({
  title: Yup.string()
    .required("Title is required")
    .trim()
    .max(100, "Title must be at most 100 characters long"),

  body: Yup.string()
    .required("Body is required")
    .trim()
    .max(500, "Body must be at most 500 characters long"),

  device_type: Yup.array()
    .required("Device type is required")
    .min(1, "At least one device type must be selected"),
});

export const customerReportSchema = [
  Yup.object().shape({
    reason: Yup.string()
      .label("report reason")
      .trim()
      .required("Please select your report reason"),
  }),
  Yup.object().shape({
    description: Yup.string()
      .label("description")
      .trim()
      .required("Description is required")
      .min(10, "Minimum 10 characters are required")
      .max(200, "Maximum 200 characters are required"),
  }),
];

export const faqsCategorySchema = Yup.object().shape({
  name: Yup.string()
    .label("category_name")
    .trim()
    .required("Category name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),
  // .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
});

export const groupSchema = Yup.object().shape({
  name: Yup.string()
    .label("group_name")
    .trim()
    .required("Group name is required")
    .min(3, "Minimum 3 characters are required")
    .max(50, "Maximum 50 characters are allowed"),
});

export const salesMemberSchema = Yup.object().shape({
  first_name: Yup.string()
    .label("First Name")
    .trim()
    .required("First name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z\s]*$/, "Special characters are not allowed"),
  last_name: Yup.string()
    .label("Last Name")
    .trim()
    .required("Last name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z]*$/, "Special characters and spaces are not allowed"),
  contact_number: validationValues.ContactNumber,
  email: validationValues.EmailAddress,
  location: Yup.string()
    .label("Location")
    .trim()
    .required("Location is required")
    .min(3, "Minimum 3 characters are required"),
  state_id: Yup.object().nullable().required("Please select a state"),
  city_id: Yup.object().nullable().required("Please select a city"),
});

export const walkInLeadSchema = Yup.object().shape({
  full_name: validationValues.FullNameSchema,
  email: validationValues.EmailAddress,
  contact_number: validationValues.ContactNumber,
  purchase_purpose: Yup.object().nullable().required("Please select a purpose"),
  sales_team: Yup.object().nullable().required("Please select a sales team"),
  interest_status: Yup.object()
    .nullable()
    .required("Please select a interest status"),
  type: Yup.object().nullable().required("Please select a type"),
  lead_name: Yup.string().label("Lead").trim().required(),
});

export const newContactUsScheme = Yup.object().shape({
  name: Yup.string()
    .label("full name")
    .trim()
    .required("Name is required")
    .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
  email: validationValues.EmailAddress,
  subject: Yup.string().label("Subject").required("Subject is required"),
  message: Yup.string()
    .label("Message")
    .required("Message is required")
    .min(5, "Minimum 5 characters required")
    .max(250, "250 digit are required"),
});

export const leadEditSchema = Yup.object().shape({
  payment: Yup.string()
    .label("Payment")
    .trim()
    .required("Payment is required")
    .matches(/^(?!0)\d+$/, "Payment amount cannot be 0."),
  deal_closed_amount: Yup.string()
    .label("Deal Closed Amount")
    .required("Deal Closed Amount is required")
    .matches(/^(?!0)\d+$/, "Deal closed amount cannot be 0."),
  payment_status: Yup.object()
    .label("Payment Status")
    .required("Payment status is required"),
  payment_mode: Yup.object()
    .label("Payment Mode")
    .required("Payment mode is required"),
  purchase_purpose: Yup.object()
    .label("Purchase Purpose")
    .required("Purchase purpose is required"),
  next_payment_date: Yup.string()
    .nullable()
    .test("not empty", "Date is required", function (value, context) {
      if (context?.parent?.payment_status?.value !== 4) {
        return !!value;
      }
      return true; // If `checked` is false, skip this validation
    }),
  next_payment_amount: Yup.string()
    .nullable()
    .matches(/^(?!0)\d+$/, "Next payment amount cannot be 0.")
    .test(
      "not empty",
      "Next payment amount is required",
      function (value, context) {
        if (context?.parent?.payment_status?.value !== 4) {
          return !!value;
        }
        return true; // If `checked` is false, skip this validation
      }
    ),
});

export const userProfileSchema = Yup.object().shape({
  first_name: Yup.string()
    .label("First Name")
    .trim()
    .required("First name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z\s]*$/, "Special characters are not allowed"),
  last_name: Yup.string()
    .label("Last Name")
    .trim()
    .required("Last name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z]*$/, "Special characters and spaces are not allowed"),
  contact_number: validationValues.ContactNumber,
  email: validationValues.EmailAddress,
  location: Yup.string()
    .label("Location")
    .trim()
    .required("Location is required")
    .min(3, "Minimum 3 characters are required"),
  state_id: Yup.object().nullable().required("Please select a state"),
  city_id: Yup.object().nullable().required("Please select a city"),
});

export const adminProfileSchema = Yup.object().shape({
  first_name: Yup.string()
    .label("First Name")
    .trim()
    .required("First name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z\s]*$/, "Special characters are not allowed"),
  last_name: Yup.string()
    .label("Last Name")
    .trim()
    .required("Last name is required")
    .min(3, "Minimum 3 characters are required")
    .max(30, "Maximum 30 characters are allowed")
    .matches(/^[a-zA-Z]*$/, "Special characters and spaces are not allowed"),
  email: validationValues.EmailAddress,
});

export const requestTourSchema = [
  Yup.object().shape({
    proposedSlots: Yup.array()
      .of(
        Yup.object().shape({
          date: Yup.string().required("Date is required"),
          time: Yup.object().required("Time is required"),
        })
      )
      .min(1, "At least one proposed slot is required")
      .required("Proposed slots are required"),
  }),
  Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be at most 50 characters"),
    email: validationValues.EmailAddress,
    contact_number: validationValues.ContactNumber,
    message: Yup.object().required("Message is required"),
    is_intrested_financial_information: Yup.boolean(),
  }),
];

export const salesDataSchema = Yup.object({
  builder_firstname: Yup.string()
    .optional()
    .matches(
      /^[a-zA-Z]+$/,
      "Builder name must only contain letters without spaces"
    )
    .max(20, "Builder name must not exceed 20 characters"),

  builder_lastname: Yup.string()
    .optional()
    .matches(
      /^[a-zA-Z]+$/,
      "Builder name must only contain letters without spaces"
    )
    .max(20, "Builder name must not exceed 20 characters"),

  builder_email: validationValues.EmailAddress,

  builder_number: validationValues.ContactNumber,

  builder_address: Yup.string()
    .optional()
    .max(200, "Address must not exceed 200 characters"),

  sales_person_firstname: Yup.string()
    .optional()
    .matches(
      /^[a-zA-Z]+$/,
      "sales first name must only contain letters without spaces"
    )
    .max(20, "sales first name must not exceed 20 characters"),

  sales_person_lastname: Yup.string()
    .optional()
    .matches(
      /^[a-zA-Z]+$/,
      "sales las tname must only contain letters without spaces"
    )
    .max(20, "sales last name must not exceed 20 characters"),

  sales_person_number: validationValues.ContactNumber,

  civil_engineer_firstname: Yup.string()
    .optional()
    .matches(
      /^[a-zA-Z]+$/,
      "civil firstname must only contain letters without spaces"
    )
    .max(20, "civil firstname must not exceed 20 characters"),

  civil_engineer_lastname: Yup.string()
    .optional()
    .matches(
      /^[a-zA-Z]+$/,
      "civil lastname must only contain letters without spaces"
    )
    .max(20, "civil lastname must not exceed 20 characters"),

  civil_engineer_number: validationValues.ContactNumber,

  project_name: Yup.string().max(
    100,
    "Project name must not exceed 100 characters"
  ),

  project_address: Yup.string().max(
    200,
    "Project address must not exceed 200 characters"
  ),

  project_brochure: Yup.mixed().optional(),

  project_photos: Yup.array()
    .optional()
    .max(20, "You can upload up to 20 photos"),

  other: Yup.string()
    .optional()
    .min(10, "other details must not exceed 10 characters")
    .max(500, "other details must not exceed 500 characters"),
});

export const advertiseSchema = Yup.object({
  full_name: validationValues.FullNameSchema,
  email: validationValues.EmailAddress,
  address: Yup.string()
    .label("address")
    .trim()
    .required("Address is required field")
    .min(3, "minimum 3 characters are required"),
  mobile: validationValues.ContactNumber,
  role: Yup.object().required("Roles is required field"),
  message: Yup.string()
    .label("Message")
    .trim()
    .required("Message is required")
    .min(3, "Minimum 3 characters are required")
    .max(500, "Maximum 500 characters are allowed"),
});

export const seoValidation = Yup.object({
  page: Yup.string().trim().required("Page name is required field"),
  title: Yup.string().trim().required("Title is required field"),
  route: Yup.string().trim().required("Route is required field"),
  keywords: Yup.string().trim().required("Keywords is required field"),
  link: Yup.string(),
  description: Yup.string(),
});

export const areaValidation = Yup.object({
  state: Yup.object().required("State is required field"),
  city: Yup.object().required("City is required field"),
  area: Yup.string()
    .trim()
    .required("Area is required field")
    .matches(/^[a-zA-Z0-9\s]+$/, "Special characters are not allowed"),
});

export const sendWhatAppMessageSchema = Yup.object({
  template: Yup.object().required("Template is a required field"),
  sender_number: Yup.array()
    .of(
      Yup.object({
        label: Yup.string().required("Label is required"),
        value: Yup.string().required("Value is required"),
      })
    )
    .min(1, "At least one member is required")
    .required("Member is a required field"),
});
